<template>
  <div id="controls-container">
    <!-- Top row: test ID on the left, language switcher on the right -->
    <div class="header-row">
      <div id="test-info"></div>
      <div id="language-switcher-container">
        <select id="language-switcher" class="language-switcher" :aria-label="$t('tooltip.language')"></select>
      </div>
    </div>

    <!-- Row 1: annotation tools (configured before annotating, less frequent) -->
    <div class="navbar-row row-tools">
      <a id="toolbox"></a>

      <div id="versionDropdownContainer"></div>

      <div id="changueClassContainer"></div>

      <div id="button-showall-container" class="tooltip-container">
        <button id="show_all" type="button" class="styled-button">
          <img src="@/assets/showall_icon.png" alt="Clickable Image" style="width: 35px; height: 20px;">
        </button>
        <span id="tooltip-showall" class="tooltip"></span>
      </div>

      <div id="button-showtextbox-container" class="tooltip-container">
        <button id="show_textbox" type="button" class="styled-button">
          <img src="@/assets/showtextbox_icon.png" alt="Clickable Image" style="width: 25px; height: 25px;">
        </button>
        <span id="tooltip-showtextbox" class="tooltip"></span>
      </div>

      <div id="button-delete-container" class="tooltip-container">
        <button id="delete" type="button" class="styled-button2">
          <img src="@/assets/delete_icon.png" alt="Clickable Image" class="white-image" style="width: 15px; height: 15px;">
        </button>
        <span id="tooltip-delete" class="tooltip"></span>
      </div>

      <div id="button-radiomics-container" class="tooltip-container right-tools-anchor">
        <button id="show_radiomics" type="button" class="styled-button radiomics-button" aria-label="Show radiomics panel">RAD</button>
        <span id="tooltip-radiomics" class="tooltip"></span>
        <div id="radiomics-popover" class="radiomics-popover" style="display: none;"></div>
      </div>

      <div id="button-metadata-container" class="tooltip-container metadata-container-right">
        <button id="show_metadata" type="button" class="styled-button info-button" :aria-label="$t('tooltip.metadata')">i</button>
        <span id="tooltip-metadata" class="tooltip"></span>
        <div id="metadata-popover" class="metadata-popover" style="display: none;"></div>
      </div>
    </div>

    <!-- Row 2: navigation + save (used constantly while annotating) -->
    <div class="navbar-row row-actions">
      <div id="frame-slider-container" ref="sliderContainer">
        <button id="prev" type="button" class="arrow-button">&lt;</button>
        <input
          type="range"
          id="frame-slider"
          :min="0"
          :max="maxFrames"
          v-model="currentFrame"
          @input="onFrameSliderChange"
        />
        <button id="next" type="button" class="arrow-button">&gt;</button>
        <span id="frame-info">{{ $t('frame_info', { current: currentFrame, max: maxFrames }) }}</span>
        <input type="text" id="frame">

        <div id="button-goFrame-container" class="tooltip-container">
          <button id="go" type="button" class="styled-button1">
            <img src="@/assets/arrow_icon.png" alt="Clickable Image" class="white-image" style="width: 15px; height: 15px;">
          </button>
          <span id="tooltip-goFrame" class="tooltip"></span>
        </div>
      </div>

      <div id="button-save-container" class="tooltip-container">
        <button id="saveAnnotations" type="button" class="styled-button1">
          <img src="@/assets/save_icon.png" alt="Clickable Image" class="white-image" style="width: 15px; height: 15px;">
        </button>
        <span id="tooltip-save" class="tooltip"></span>
      </div>

      <div id="button-download-container" class="tooltip-container">
        <button id="loadAnnotations" type="button" class="styled-button1" style="display:none">
          <img src="@/assets/download_icon.png" alt="Clickable Image" class="white-image" style="width: 15px; height: 15px;">
        </button>
        <span id="tooltip-download" class="tooltip"></span>
      </div>

      <img
        id="loading-gif"
        src="@/assets/loading.gif"
        alt="Loading..."
        style="display: none; width: 45px; height: 45px; margin-left: 10px; vertical-align: middle;"
      >
    </div>
  </div>

  <div id="content" ></div>

  <div id="loading-indicator" class="loading-text">{{ $t('loading_dicom') }}<span id="loading-dots">.</span></div>

</template>

<script type="module" src="./CornerstoneTest.js"> </script>

<style>
html, body {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#content {
  width: 100%; /* Ajusta el ancho al contenedor padre */
  height: 100%; /* Ajusta la altura */
  max-width: 90vw; /* Ocupa el 90% del ancho de la ventana */
  max-height: 90vh; /* Ocupa el 90% de la altura de la ventana */
  margin: auto; /* Centrado */
  overflow: auto; /* Permite scroll interno si excede el tamaño */
  display: flex; /* Usa Flexbox para centrar contenido */
  justify-content: center;
  align-items: center;
  border: 1px solid #ccc; /* Borde para visualizar el límite */
}
h3 {
margin: 40px 0 0;
}
ul {
list-style-type: none;
padding: 0;
}
li {
display: inline-block;
margin: 0 10px;
}
a {
margin-left: 10px;
margin-right: 10px;
}
input {
text-align: right;
}

#controls-container {
  position: fixed;       /* Ancla el contenedor */
  top: 0;                /* Posición en la parte superior */
  left: 0;               /* Alineado a la izquierda */
  width: 100%;           /* Que ocupe todo el ancho */
  z-index: 1000;         /* Que esté por encima de otros elementos */
  background-color: white;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
}

/* Top header: test ID on the left, language switcher on the right */
.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 10px 0 10px;
  box-sizing: border-box;
}
#test-info {
  font-weight: bold;
  font-size: 15px;
  text-align: left;
}
#language-switcher-container {
  flex-shrink: 0;
}

.navbar-row {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 6px 10px;
  flex-wrap: nowrap;        /* Cada row se queda en una sola línea */
  /* sin overflow: scroll/auto — esto crearía un scroll container que recortaría
     popovers absolutamente posicionados (p.ej. el de metadata) */
}
.navbar-row.row-tools {
  border-bottom: 1px solid #eee;  /* Separador visual sutil entre filas */
}
.navbar-row.row-actions {
  padding: 2px 10px;        /* Fila de acciones más compacta */
  overflow-x: visible;      /* Sin scroll: el slider absorbe el espacio sobrante */
}
.navbar-row.row-actions #frame-slider-container {
  flex: 1;                  /* El slider se come el ancho libre */
  min-width: 0;             /* Permite que el contenedor se encoja sin desbordar */
}
.navbar-row.row-actions #frame {
  width: 50px;              /* El input numérico no necesita más */
}

/* #toolbox es un <a>, no tiene flex por defecto.
   Forzamos flex horizontal para que los dropdowns (clase + herramienta) vayan en línea. */
#toolbox {
  display: flex;
  align-items: center;
  gap: 10px;
}

#frame-slider-container {
  display: flex;
  align-items: center;
  gap: 10px;
}
#frame-slider {
  flex: 1;                  /* Que el slider ocupe lo que pueda dentro de su contenedor */
  height: 10px;
  min-width: 80px;          /* Mínimo cómodo, pero permite encogerse en pantallas estrechas */
}
.arrow-button {
  background-color: transparent; /* Fondo transparente */
  border: none; /* Sin borde */
  font-size: 24px; /* Tamaño de la flecha */
  color: #007bff; /* Azul similar al slider */
  cursor: pointer; /* Cambiar el cursor a mano */
  padding: 0 10px; /* Espaciado alrededor de la flecha */
  display: flex; /* Para alinear verticalmente */
  align-items: center;
}
.arrow-button:hover {
  color: #0056b3; /* Azul más oscuro al pasar el mouse */
}


/* Estilo del tooltip */
/* Contenedor para el botón */
.tooltip-container {
  position: relative; /* Permite posicionar el tooltip relativo al contenedor */
  display: inline-block;
}

/* Tooltip personalizado */
.tooltip {
  position: absolute;
  bottom: 120%; /* Coloca el tooltip encima del botón */
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8); /* Fondo oscuro */
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 14px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
  z-index: 1000;
}

.tooltip.active {
  opacity: 1;
  visibility: visible;
}

/* Botón estilizado */
.styled-button1 {
  background-color: #efefef; /* Color de fondo */
  border: 1px solid #ccc;   /* Borde */
  border-radius: 5px;       /* Bordes redondeados */
  padding: 10px;            /* Espaciado interno */
  cursor: pointer;          /* Cambiar el cursor a mano */
  display: inline-flex;     /* Para alinear la imagen dentro del botón */
  align-items: center;      /* Centrar verticalmente */
  justify-content: center;  /* Centrar horizontalmente */
  transition: background-color 0.3s ease; /* Transición para hover */
}

/* Cambiar el color de fondo al pasar el ratón */
.styled-button1:hover {
  background-color: #efefefaa; /* Color de fondo al pasar el ratón */
}


.styled-button1 {
  background-color: #007bff; /* Color de fondo */
  border: 1px solid #ccc;   /* Borde */
  border-radius: 5px;       /* Bordes redondeados */
  padding: 10px;            /* Espaciado interno */
  cursor: pointer;          /* Cambiar el cursor a mano */
  display: inline-flex;     /* Para alinear la imagen dentro del botón */
  align-items: center;      /* Centrar verticalmente */
  justify-content: center;  /* Centrar horizontalmente */
  transition: background-color 0.3s ease; /* Transición para hover */
}

/* Cambiar el color de fondo al pasar el ratón */
.styled-button1:hover {
  background-color: #0056b3; /* Color de fondo al pasar el ratón */
}

.styled-button2 {
  background-color: #ff4949; /* Color de fondo */
  border: 1px solid #ccc;   /* Borde */
  border-radius: 5px;       /* Bordes redondeados */
  padding: 10px;            /* Espaciado interno */
  cursor: pointer;          /* Cambiar el cursor a mano */
  display: inline-flex;     /* Para alinear la imagen dentro del botón */
  align-items: center;      /* Centrar verticalmente */
  justify-content: center;  /* Centrar horizontalmente */
  transition: background-color 0.3s ease; /* Transición para hover */
}

/* Cambiar el color de fondo al pasar el ratón */
.styled-button2:hover {
  background-color: #ff4949aa; /* Color de fondo al pasar el ratón */
}

/* Estilos para botones deshabilitados */
.styled-button1:disabled,
.styled-button2:disabled {
  filter: brightness(0.7); /* Reduce el brillo para dar un tono más gris */
  cursor: not-allowed; /* Muestra el cursor de "no permitido" */
  opacity: 0.8; /* Opcional: Reduce la opacidad ligeramente */
}

/* Evita el efecto hover en botones deshabilitados */
.styled-button1:disabled:hover,
.styled-button2:disabled:hover {
  filter: brightness(0.7); /* Mantiene el tono más gris al pasar el ratón */
}

.white-image {
  filter: invert(1) brightness(2);
}

/* Empuja el contenedor del botón "info" (y selector de idioma) al extremo derecho de la fila */
.metadata-container-right {
  margin-left: auto;
}

/* Selector de idioma compacto (ES / GL / EN) */
.language-switcher {
  height: 28px;
  padding: 0 4px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
  text-align-last: center;        /* centra el valor seleccionado en algunos navegadores */
}
.language-switcher:hover {
  background-color: #f5f5f5;
}

/* Empuja el primer botón del grupo derecho (radiomics) al extremo derecho.
   El botón "info" lo sigue inmediatamente porque ambos viven al final del row. */
.right-tools-anchor {
  margin-left: auto;
}

/* Botón "RAD" para abrir el popover de radiómica — cuadrado para
   diferenciarlo visualmente del redondo "i" que va al lado. */
.radiomics-button {
  width: auto;
  min-width: 38px;
  height: 32px;
  border-radius: 5px;
  background-color: #efefef;
  border: 1px solid #ccc;
  font-family: sans-serif;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  color: #007bff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  transition: background-color 0.2s ease;
}
.radiomics-button:hover {
  background-color: #e0e0e0;
}

/* Popover de radiómica. Mismo patrón que metadata: contenedor con data-v
   de Vue, contenido interno generado por innerHTML (estilos no-scoped). */
.radiomics-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  padding: 12px 16px;
  min-width: 380px;
  max-width: 520px;
  max-height: 75vh;
  overflow-y: auto;
  z-index: 2000;
  font-size: 13px;
  color: #2c3e50;
  text-align: left;
}

/* Botón "info" para abrir el popover de metadata */
.info-button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #efefef;
  border: 1px solid #ccc;
  font-family: serif;
  font-style: italic;
  font-weight: bold;
  font-size: 18px;
  color: #007bff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.2s ease;
}
.info-button:hover {
  background-color: #e0e0e0;
}

/* Popover anclado bajo el botón "info" — sólo el contenedor está aquí porque
   está renderizado por el template y sí recibe el data-v de Vue. El interior
   del popover se genera dinámicamente con innerHTML, así que sus estilos van
   en el bloque <style> no-scoped más abajo. */
.metadata-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;                 /* Anclado al borde derecho del botón (que ahora está a la derecha del row) */
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  padding: 12px 16px;
  min-width: 320px;
  max-width: 420px;
  max-height: 70vh;
  overflow-y: auto;
  z-index: 2000;
  font-size: 13px;
  color: #2c3e50;
  text-align: left;
}

/* Loading text styles */
.loading-text {
  font-size: 1.2rem;
  color: black;
  text-align: center;
  margin-top: 20px;
  font-weight: bold;
  animation: fadein 0.5s ease-in-out;
}

#loading-indicator {
  position: absolute;         /* Posición absoluta para ocupar toda la pantalla */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%); /* Centrado exacto */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  z-index: 9999;
}

@keyframes fadein {
  from { opacity: 0; }
  to { opacity: 1; }
}

</style>

<!-- Estilos no-scoped para el contenido del popover de metadata + los viewports
     de Cornerstone. Estos elementos se crean con document.createElement desde JS,
     no llevan el data-v de Vue y los selectores de <style scoped> no les aplican. -->
<style>
/* Cursor visible sobre los viewports. Cornerstone pone `cursor: none` inline
   cuando hay herramientas de anotación activas (para dibujar su propio cursor
   SVG que no siempre se ve). Forzamos crosshair para no perder de vista dónde
   está el puntero mientras se dibuja o modifica un contorno. */
#axialViewport,
#sagittalViewport,
#coronalViewport,
#axialViewport canvas,
#sagittalViewport canvas,
#coronalViewport canvas,
#axialViewport svg,
#sagittalViewport svg,
#coronalViewport svg {
  cursor: crosshair !important;
}
</style>

<style>
.metadata-popover h4 {
  margin: 12px 0 4px 0;
  font-size: 11px;
  color: #007bff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #eee;
  padding-bottom: 2px;
}
.metadata-popover h4:first-child {
  margin-top: 0;
}
.metadata-popover dl {
  margin: 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  row-gap: 3px;
  align-items: baseline;
}
.metadata-popover dt {
  font-weight: 700;
  color: #2c3e50;
}
.metadata-popover dd {
  margin: 0;
  color: #555;
  word-break: break-word;
}

/* ===== Radiomics popover content (innerHTML, sin data-v de Vue) ===== */

.radiomics-popover .radiomics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.radiomics-popover .radiomics-title {
  margin: 0;
  font-size: 14px;
  color: #007bff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.radiomics-popover .radiomics-refresh {
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  line-height: 1;
}
.radiomics-popover .radiomics-refresh:hover {
  background: #f0f0f0;
}
/* ----- Configuración (params pyRadiomics) ----- */
.radiomics-popover .radiomics-config {
  margin-bottom: 10px;
}
.radiomics-popover .radiomics-config-toggle {
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  color: #2c3e50;
}
.radiomics-popover .radiomics-config-toggle:hover {
  background: #ececec;
}
.radiomics-popover .radiomics-config-open {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  background: #fafafa;
}
.radiomics-popover .radiomics-config-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 8px;
}
.radiomics-popover .radiomics-feature-count {
  font-size: 11px;
  color: #007bff;
  white-space: nowrap;
}
.radiomics-popover .radiomics-profile-row {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-bottom: 6px;
}
.radiomics-popover .radiomics-profile-row label {
  font-size: 11px;
  font-weight: 600;
  color: #555;
}
.radiomics-popover .radiomics-profile-row select,
.radiomics-popover .radiomics-profile-row input {
  flex: 1;
  font-size: 12px;
  padding: 3px 6px;
  min-width: 0;
}
.radiomics-popover .radiomics-profile-row button {
  background: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 3px 8px;
  font-size: 11px;
  cursor: pointer;
}
.radiomics-popover .radiomics-group {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #eee;
}
.radiomics-popover .radiomics-group-title {
  font-size: 11px;
  font-weight: 700;
  color: #007bff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.radiomics-popover .radiomics-row2 {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  margin-bottom: 3px;
}
.radiomics-popover .radiomics-row3 {
  display: grid;
  grid-template-columns: 120px 1fr 1fr 1fr;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  margin-bottom: 3px;
}
.radiomics-popover .radiomics-row2 input[type="number"],
.radiomics-popover .radiomics-row2 input[type="text"],
.radiomics-popover .radiomics-row2 select,
.radiomics-popover .radiomics-row3 input[type="number"],
.radiomics-popover .radiomics-row3 input[type="text"],
.radiomics-popover .radiomics-row3 select {
  width: 100%;
  padding: 3px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  min-width: 0;
}
.radiomics-popover .radiomics-checkgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  font-size: 12px;
}
.radiomics-popover .radiomics-imagetype {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.radiomics-popover .radiomics-imagetype input[type="text"],
.radiomics-popover .radiomics-imagetype select {
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
}
.radiomics-popover .radiomics-advanced-toggle {
  margin-top: 8px;
  background: #eef4ff;
}

/* ----- From-class section ----- */
.radiomics-popover .radiomics-fromclass {
  margin-bottom: 10px;
}
.radiomics-popover .radiomics-fromclass-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 6px;
  align-items: center;
}
.radiomics-popover .radiomics-fromclass-row select,
.radiomics-popover .radiomics-fromclass-row input {
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
  min-width: 0;
}

/* ----- Drawing section (Nueva ROI / ROI en curso) ----- */
.radiomics-popover .radiomics-drawing {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}
.radiomics-popover .radiomics-drawing:empty {
  display: none;
}
.radiomics-popover .radiomics-new {
  display: flex;
  gap: 6px;
  align-items: stretch;
}
.radiomics-popover .radiomics-new input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
  min-width: 0;
}
.radiomics-popover .radiomics-new-hint {
  margin-top: 6px;
  font-size: 11px;
  color: #888;
  line-height: 1.4;
}
.radiomics-popover .radiomics-drawing-active {
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  padding: 10px 12px;
}
.radiomics-popover .radiomics-drawing-title {
  font-weight: 700;
  color: #d46b08;
  font-size: 13px;
  margin-bottom: 4px;
}
.radiomics-popover .radiomics-drawing-title code {
  background: #ffe7ba;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
}
.radiomics-popover .radiomics-drawing-count {
  font-size: 12px;
  color: #555;
  margin-bottom: 8px;
}
.radiomics-popover .radiomics-drawing-actions {
  display: flex;
  gap: 6px;
}
.radiomics-popover .radiomics-primary {
  background: #007bff;
  color: white;
  border: 1px solid #0056b3;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.radiomics-popover .radiomics-primary:hover:not(:disabled) {
  background: #0056b3;
}
.radiomics-popover .radiomics-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.radiomics-popover .radiomics-secondary {
  background: #f0f0f0;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}
.radiomics-popover .radiomics-secondary:hover:not(:disabled) {
  background: #e0e0e0;
}
.radiomics-popover .radiomics-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.radiomics-popover .radiomics-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.radiomics-popover .radiomics-empty,
.radiomics-popover .radiomics-loading {
  padding: 16px 8px;
  text-align: center;
  color: #888;
  font-style: italic;
}
.radiomics-popover .radiomics-error {
  padding: 8px;
  color: #b00020;
  background: #fde8ea;
  border-radius: 4px;
  font-size: 12px;
}
.radiomics-popover .radiomics-footer {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #eee;
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.radiomics-popover .radiomics-row {
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 8px 10px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 8px;
  row-gap: 4px;
}
.radiomics-popover .radiomics-row-name {
  font-weight: 700;
  color: #2c3e50;
  font-size: 14px;
}
.radiomics-popover .radiomics-row-meta {
  font-size: 11px;
  color: #666;
  line-height: 1.45;
}
.radiomics-popover .radiomics-hash {
  font-family: monospace;
  font-size: 10px;
  color: #999;
}
.radiomics-popover .radiomics-row-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: stretch;
}
.radiomics-popover .radiomics-row-actions button {
  background: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 30px;
  height: 26px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}
.radiomics-popover .radiomics-row-actions button:hover {
  background: #e8e8e8;
}
.radiomics-popover .radiomics-row-actions .radiomics-danger:hover {
  background: #fde8ea;
}

.radiomics-popover .radiomics-detail {
  grid-column: 1 / span 2;
}
.radiomics-popover .radiomics-detail:empty {
  display: none;
}
.radiomics-popover .radiomics-detail-meta {
  font-size: 11px;
  color: #666;
  padding: 4px 0 8px 0;
  border-bottom: 1px solid #eee;
  margin-bottom: 8px;
}
.radiomics-popover .radiomics-detail-meta code {
  background: #f5f5f5;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
}
.radiomics-popover details.radiomics-family {
  margin-top: 6px;
}
.radiomics-popover details.radiomics-family > summary {
  font-weight: 700;
  color: #007bff;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 2px 0;
  border-bottom: 1px solid #eee;
}
.radiomics-popover details.radiomics-family > dl {
  margin: 4px 0 0 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  row-gap: 2px;
  font-size: 11px;
}
.radiomics-popover details.radiomics-family dt {
  font-family: monospace;
  font-size: 10px;
  color: #2c3e50;
  font-weight: 500;
  word-break: break-all;
}
.radiomics-popover details.radiomics-family dd {
  margin: 0;
  font-family: monospace;
  text-align: right;
  color: #007bff;
}
</style>
